/**
 * Why a supplier option cannot be applied to THIS quotation line — in words.
 *
 * The number itself is the server's (`supplierOptions.priceForLineUnit`,
 * BUSINESS_RULES#supplier-option-price-per-line-unit): the dropdown request
 * names the line's unit, and every option comes back with `lineUnitPrice`, or
 * null and a `lineUnitRefusal` code. This file only turns those codes into
 * copy. It never re-derives a price, and it never shows a server sentence
 * (invariant 6) — the codes are the contract.
 *
 * QUO-00068 is why the refusal exists: TT$12.50 per 10 kg BAG was applied to a
 * 600 KG line as TT$12.50 a kilo. A bag with a size and no count is not a known
 * weight (DECISIONS D13), so that row is shown with its own figure and cannot be
 * clicked.
 */
import type { SupplierDropdownItem } from '../../lib/vendorInvoiceApi';
import { LOCKED_UNIT_CODE } from '../../utils/pricing';
import { LOCKED_LINE_UNLOCK_MESSAGE } from './statedSelling';

const unitName = (u: string | null | undefined): string => String(u || '').trim().toUpperCase();

function refusalCopy(reason: string | null | undefined, optionUnit: string, lineUnit: string): string {
  switch (reason) {
    case 'NO_STATED_RATIO':
      return optionUnit && lineUnit
        ? `Priced per ${optionUnit}, and this line is quoted per ${lineUnit}. Nothing on the source says how many ${lineUnit} one ${optionUnit} holds, so this price cannot be applied without guessing. Enter the cost by hand, or add the pack size where this price came from and reopen the list.`
        : 'This price is stated per a different unit from this line, and nothing on the source says how the two convert, so it cannot be applied without guessing. Enter the cost by hand.';
    case 'OPTION_UNIT_NOT_STATED':
      return `This source does not say which unit its price is per, so it cannot be applied to a line quoted per ${lineUnit || 'a unit'}.`;
    case 'LINE_UNIT_NOT_STATED':
      return "This line has no unit, so a supplier's price cannot be stated per it. Set the line's unit first.";
    case 'OPTION_PRICE_NOT_STATED':
      return 'This source states no price.';
    default:
      return 'This supplier price cannot be applied to this line.';
  }
}

/**
 * Null when the option has a price per the line's unit; otherwise the reason it
 * cannot be applied.
 *
 * No real, POSITIVE native price wins first, before any per-line
 * restatement: a source stating no price at all — or one whose figure is
 * zero or less, whichever field carries that — is refused here, in words,
 * rather than reaching `priceForLineUnit`'s own null check as a bare $0.00
 * that answers `lineUnitPrice: 0`, a real, applicable-looking price (owner,
 * 2026-09-24; Cabbage White / Grapes White / Leeks White were clickable at
 * exactly that $0.00). Checked directly here (`<= 0`), not only relied on
 * from `vendorInvoiceApi`'s DTO mapping (`positiveNumber` already folds a
 * genuinely-stated $0.00 into null there too) — this function is the one
 * place both the panel and a supplier pick ask "can this be applied", and it
 * must refuse a non-positive price on its own, not merely inherit an
 * upstream mapper's discipline.
 *
 * An option with NO line price at all — a response that did not answer for the
 * line, not merely one that answered null — is refused too. A price that was
 * never stated per this line's unit is exactly what QUO-00068 applied.
 */
export function supplierOptionRefusal(option: SupplierDropdownItem): string | null {
  if (typeof option.unitPrice !== 'number' || !Number.isFinite(option.unitPrice) || option.unitPrice <= 0) {
    return 'This source states no price.';
  }
  if (typeof option.lineUnitPrice === 'number' && Number.isFinite(option.lineUnitPrice)) return null;
  // `priceUnit` before `uom`: it is the unit the server priced the row per and
  // the one its Unit Price column prints. `uom` is the cell the invoice printed,
  // and the reviewed Pack Unit outranks it (NAISA, QA 2026-09-22: printed BALE,
  // reviewed CASE) — naming it here would put "/CASE" and "Priced per BALE" on
  // one row.
  return refusalCopy(option.lineUnitRefusal, unitName(option.priceUnit || option.uom), unitName(option.lineUnit));
}

/** Copy for a supplier pick the server refused, keyed on its code alone. */
export function supplierPickErrorMessage(err: unknown): string {
  const e = (err && typeof err === 'object' ? err : {}) as { code?: unknown; reason?: unknown };
  switch (e.code) {
    case 'SUPPLIER_PRICE_NOT_RESTATABLE':
      return refusalCopy(typeof e.reason === 'string' ? e.reason : null, '', '');
    case 'SUPPLIER_OPTION_NOT_FOUND':
      return "That supplier's price is no longer listed for this item. Reopen the supplier list and pick again.";
    case 'SUPPLIER_PRICE_CURRENCY_UNSUPPORTED':
      return "That supplier's price is in a currency this quotation holds no rate for, so it cannot be applied.";
    case LOCKED_UNIT_CODE:
      return LOCKED_LINE_UNLOCK_MESSAGE;
    case 'SUPPLIER_SOURCE_UNAVAILABLE':
      return 'Some supplier data could not be read just now, so nothing was changed. Try again in a moment.';
    case 'SUPPLIER_OPTION_REQUIRED':
      return 'The supplier list on this page is out of date. Reload the page and pick again.';
    default:
      return 'The supplier could not be changed.';
  }
}

/**
 * How far apart two suppliers' prices for one line may sit before the list says
 * so: ten times, either way.
 *
 * DECISIONS D42 reads a pack with a size and no count as ONE of that size, and
 * the prod dry run of its repair (2026-09-17) found the cost of a pack whose
 * count nobody typed: "Soft Drinks" as a 330 ML case prices a litre about 24x
 * what it costs, a 14-bag tortilla case 14x. Real suppliers of one product do
 * not sit ten times apart, so a price that far out is almost always a pack read
 * wrong — on that row, or on the others.
 */
export const PRICE_OUTLIER_FACTOR = 10;

export interface SupplierPriceOutlier {
  /** How many times the others' price (rounded), whichever side it is on. */
  times: number;
  direction: 'above' | 'below';
}

/**
 * The options whose price for the line is at least PRICE_OUTLIER_FACTOR times
 * the median of the OTHER usable options, above or below — by index into
 * `prices`. `prices[i]` is option i's price per the line's unit in one currency
 * (the page's US$ figure), or null when the option cannot be applied, and a
 * null never counts on either side.
 *
 * A warning, never a refusal: the row stays clickable. With only two options
 * both are flagged — one of them is wrong, and nothing here can say which.
 */
export function supplierPriceOutliers(prices: Array<number | null | undefined>): Map<number, SupplierPriceOutlier> {
  const out = new Map<number, SupplierPriceOutlier>();
  const usable = prices
    .map((price, index) => ({ price, index }))
    .filter((p): p is { price: number; index: number } => typeof p.price === 'number' && Number.isFinite(p.price) && p.price > 0);
  if (usable.length < 2) return out;
  for (const { price, index } of usable) {
    const others = usable.filter((p) => p.index !== index).map((p) => p.price).sort((a, b) => a - b);
    const mid = Math.floor(others.length / 2);
    const median = others.length % 2 ? others[mid] : (others[mid - 1] + others[mid]) / 2;
    const ratio = price / median;
    if (ratio >= PRICE_OUTLIER_FACTOR) out.set(index, { times: Math.round(ratio), direction: 'above' });
    else if (ratio <= 1 / PRICE_OUTLIER_FACTOR) out.set(index, { times: Math.round(1 / ratio), direction: 'below' });
  }
  return out;
}
